/**
 * v7.33 — Blindagem de Entrega via PIN.
 *
 * Regras:
 *  - Ao mudar para 'delivering', um PIN de 4 dígitos é gerado e salvo no pedido.
 *  - Para finalizar ('delivered'), o entregador precisa digitar o PIN exato
 *    que o cliente vê na tela de rastreio.
 *  - Helpers são offline-first (escrevem em Dexie; sync via Firebase mantém
 *    o estado consistente entre dispositivos).
 *
 * Erros lançados são strings amigáveis prontas para `toast.error()`.
 */
import db from '@/db';

/**
 * Gera um PIN de 4 dígitos criptograficamente seguro (sem viés de %10).
 *
 * v7.39 — Hardening:
 *  - Mix de 2 chamadas crypto + XOR (defense in depth, mesmo sendo desnecessário
 *    matematicamente: torna impossível RNG enganoso afetar o resultado).
 *  - Warning em console se cair no fallback Math.random (alguns browsers
 *    em contextos hostis bloqueiam getRandomValues).
 *  - Log do PIN gerado (audit trail). O cliente já recebe via WhatsApp,
 *    então não há vazamento adicional — ajuda a debugar o relato de
 *    "todos os PINs são iguais" em produção via DevTools.
 *
 * @returns {string} ex: "0742"
 */
export function generateDeliveryPin() {
  const cryptoObj = (typeof window !== 'undefined' && window.crypto)
    || (typeof globalThis !== 'undefined' && globalThis.crypto)
    || null;
  let n;
  if (cryptoObj?.getRandomValues) {
    // Mix de 2 chamadas → 64 bits de entropia destilados em 10000 valores.
    const buf = new Uint32Array(2);
    cryptoObj.getRandomValues(buf);
    n = (buf[0] ^ buf[1]) % 10000;
    if (n < 0) n += 10000; // XOR pode dar negativo se interpretado signed
  } else {
    // eslint-disable-next-line no-console
    console.warn('[PIN] window.crypto.getRandomValues indisponível — usando Math.random (entropia fraca)');
    n = Math.floor(Math.random() * 10000);
  }
  const pin = String(n).padStart(4, '0');
  // Audit trail (visível só no DevTools do operador, não vaza pro cliente)
  // eslint-disable-next-line no-console
  console.info(`[PIN] generated ${pin}`);
  return pin;
}

/**
 * Transição segura para 'delivering' — gera e persiste o PIN.
 * Se o pedido já está delivering com PIN, retorna o existente (idempotente).
 *
 * @param {number} orderId
 * @param {{ assignedTo?: number, assignedName?: string }} [opts]
 * @returns {Promise<string>} PIN de 4 dígitos
 */
export async function markOrderDelivering(orderId, opts = {}) {
  const order = await db.deliveryOrders.get(orderId);
  if (!order) throw new Error('Pedido não encontrado');
  if (order.status === 'delivered') {
    throw new Error('Pedido já foi entregue');
  }

  // Idempotente: se já tem PIN ativo, reutiliza (não regenera a cada clique).
  // v7.39 — Log explícito pra debug: se o operador vê o MESMO PIN em
  // pedidos diferentes, esse log mostra que está retornando o PIN antigo
  // do MESMO orderId — ou seja, é re-teste do mesmo pedido, não bug de RNG.
  if (order.status === 'delivering' && order.deliveryPin) {
    // eslint-disable-next-line no-console
    console.info(`[PIN] reutilizando PIN existente de pedido #${orderId}: ${order.deliveryPin} (status já era delivering)`);
    return order.deliveryPin;
  }

  const pin = generateDeliveryPin();
  // eslint-disable-next-line no-console
  console.info(`[PIN] novo PIN ${pin} atribuído ao pedido #${orderId}`);
  const patch = {
    status: 'delivering',
    deliveryPin: pin,
    deliveryPinSetAt: new Date().toISOString(),
    // v7.94 — updatedAt obrigatório: sem ele o LWW do sync rejeitava a
    // transição nos outros aparelhos (admin continuava vendo 'Pronto').
    updatedAt: new Date().toISOString(),
  };
  if (opts.assignedTo != null && order.assignedTo == null) {
    patch.assignedTo = opts.assignedTo;
    patch.assignedName = opts.assignedName || null;
    patch.assignedAt = new Date().toISOString();
  }
  await db.deliveryOrders.update(orderId, patch);
  return pin;
}

/**
 * Valida PIN e finaliza a entrega.
 *
 * @param {number} orderId
 * @param {string} providedPin — o que o entregador digitou
 * @param {{ deliveredBy?: string }} [opts]
 * @throws {Error} 'Pedido não encontrado' | 'Pedido ainda não foi despachado'
 *                | 'PIN inválido' | 'PIN é obrigatório'
 */
export async function markOrderDelivered(orderId, providedPin, opts = {}) {
  const pin = String(providedPin || '').trim();
  if (!/^\d{4}$/.test(pin)) {
    throw new Error('PIN inválido: digite os 4 dígitos');
  }
  const order = await db.deliveryOrders.get(orderId);
  if (!order) throw new Error('Pedido não encontrado');
  if (order.status === 'delivered') {
    throw new Error('Pedido já foi entregue');
  }
  if (order.status !== 'delivering' || !order.deliveryPin) {
    throw new Error('Pedido ainda não saiu para entrega — não há PIN gerado');
  }
  if (order.deliveryPin !== pin) {
    throw new Error('PIN inválido — confira com o cliente');
  }
  await db.deliveryOrders.update(orderId, {
    status: 'delivered',
    deliveredAt: new Date().toISOString(),
    deliveredBy: opts.deliveredBy || null,
    updatedAt: new Date().toISOString(),
  });
  // v7.85 (#207625) -- ARQUIVAMENTO terminal do ticket de cozinha ao entregar.
  // Remove a(s) linha(s) de kdsQueue deste pedido: o deleting hook grava
  // tombstone por stableId (imune a ressurreicao) e o ticket some da fila
  // ativa de vez. Fecha o acumulo ilimitado de kdsQueue + os fantasmas
  // pos-entrega (sugestao #3 do cliente). Best-effort: nao quebra a entrega.
  try {
    const kdsRows = await db.kdsQueue.where('orderId').equals(orderId).toArray();
    for (const row of kdsRows) {
      try { await db.kdsQueue.delete(row.id); } catch {}
    }
  } catch {}
  // v7.82 — Purga a ponte backendOrders no Firebase + claim permanente:
  // pedido finalizado com código de entrega NUNCA mais é reintroduzido
  // nas telas ativas por bridge/reconciliação. Best-effort.
  if (order.mpPaymentId) {
    import('@/lib/firebaseSync')
      .then(({ purgeBackendOrderBridge }) => purgeBackendOrderBridge(order.mpPaymentId))
      .catch(() => {});
  }
}
